import React from 'react';
import { useFormikContext, FormikErrors, FormikTouched } from 'formik';
import clsx from 'clsx';

import type { ContactFormData } from '../../FormData/config';
import type { FormFields } from '../ContactFormJSON.types';

import InputFields from '../InputFields/InputFields';

interface FieldsetsProps {
  formErrors: FormikErrors<ContactFormData>;
  formTouched: FormikTouched<ContactFormData>;
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  data: Array<{
    fields: FormFields[];
    visibleFieldsetDependencies: Array<[keyof ContactFormData, string]>;
    legend?: string;
  }>;
}

export default function Fieldsets(props: FieldsetsProps) {
  const { formErrors, formTouched, data, setFieldTouched, setFieldValue } =
    props;
  const { values } = useFormikContext<ContactFormData>();

  // Check if field || fieldset should be visible based on visibleFieldDependencies || visibleFieldsetDependencies
  function isVisible(
    visibleDependentValueArr: Array<[keyof ContactFormData, string]>
  ) {
    let boolVisible = false;

    // Loop over dependency array to check if we should show/hide the field
    visibleDependentValueArr.forEach(function (dependentValue) {
      if (
        dependentValue[1] === 'default' ||
        dependentValue[1] === values[dependentValue[0]]
      ) {
        boolVisible = true;
      }
    });

    // in the case of no dependencies, lets render all fields
    if (visibleDependentValueArr.length === 0) {
      boolVisible = true;
    }
    return boolVisible;
  }

  return (
    <>
      {data.map(function (dataObj, index) {
        // check for fieldset visibility dependencies
        if (isVisible(dataObj.visibleFieldsetDependencies)) {
          return (
            <fieldset key={`fieldset--${index}`} className="row">
              {/* render legend */}
              {dataObj.legend && (
                <legend className={clsx('col-12', 'fieldset__legend-element')}>
                  {dataObj.legend}
                </legend>
              )}

              {/* render input fields within a fieldset */}
              {dataObj.fields.map(function (field) {
                // check for field visibility dependencies
                if (
                  (field.visibleFieldDependencies &&
                    isVisible(field.visibleFieldDependencies)) ||
                  !field.visibleFieldDependencies
                ) {
                  const err = formErrors[field.name as keyof typeof formErrors];
                  return (
                    <div
                      className={clsx(
                        'fieldset__input-container',
                        'col-12',
                        `col-sm-${field.size.mobile}`,
                        `col-md-${field.size.tablet}`,
                        `col-lg-${field.size.desktop}`
                      )}
                      key={`${String(field.name)}`}
                    >
                      <InputFields
                        data={field}
                        formErrors={err && err}
                        formTouched={
                          formTouched[field.name as keyof typeof formTouched]
                        }
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  );
                }
              })}
            </fieldset>
          );
        } else {
          return null;
        }
      })}
    </>
  );
}
